import type { UploadFormData } from "./upload-form-schema";

/**
 * Discriminated narrowing of the form's `mediaInput` shape at submit time.
 *
 * The schema allows both `files` and `youtubeUrl` to coexist syntactically
 * (and a `superRefine` enforces exactly-one), but consumers want a clean
 * `switch` over what the user actually submitted. This helper does that
 * narrowing and folds in the `isYoutube` derivation that callers re-did
 * by hand in three different submit handlers.
 *
 * Both-set is schema-unreachable in practice (zodResolver blocks submit),
 * but the helper is defensive: if both are present, "files" wins so the
 * caller always processes the real media payload over the URL string.
 */
export type MediaInputKind =
  | { kind: "files"; files: File[] }
  | { kind: "url"; url: string; isYoutube: boolean }
  | { kind: "none" };

export const getMediaInputKind = (
  data: Pick<UploadFormData, "mediaInput">,
  urlInfo?: UrlInfo
): MediaInputKind => {
  const files = (data.mediaInput.files ?? []) as File[];
  const url = data.mediaInput.youtubeUrl?.trim() ?? "";

  if (files.length > 0) {
    return { kind: "files", files };
  }
  if (url) {
    return { kind: "url", url, isYoutube: urlInfo?.type === "youtube" };
  }
  return { kind: "none" };
};
